import { flattenDeep } from "lodash";

import config from "@/config";

import { beforeEnterChangeLocale, buildPathWithLang } from "./utils";

const products = config.products ?? [];

let productsRoutes = [];
let embeddedProductsRoutes = [];
let productsReportGenerateRoutes = [];

products.forEach((productConfig) => {
  const productRoutes = [];
  const embeddedProductRoutes = [];
  // Always add an Overview route.
  if (!productConfig.withoutOverview) {
    productRoutes.push({
      path: buildPathWithLang(`/products/${productConfig.productId}/overview`),
      name: `products_${productConfig.storeModule}`,
      component: () => import("../views/products/DefaultOverview.vue"),
      meta: {
        productId: productConfig.productId,
        authorize: productConfig.allowedRoles ?? config.allowedRoles,
        // If the user doesn't have the role to see it, but has the admin right for one of the
        // share class, we should still let access to it.
        allowAdminOfOneShareClass: true,
      },
      props: {
        productId: productConfig.productId,
        lockedBehindLogin: productConfig.lockedBehindLogin,
        storeModule: productConfig.storeModule,
        productName: productConfig.productName,
        productShareClassRoot: productConfig.productShareClassRoot,
        updateFrequency: productConfig.live?.updateFrequency,
        showNextSubscriptionAndRedemption: productConfig.showNextSubscriptionAndRedemption,
        timelineData: productConfig.timelineData,
      },
      beforeEnter: beforeEnterChangeLocale,
    });
  }

  // Only add the Live Analysis route if it is configured.
  if (!productConfig.withoutAnalysis && productConfig.live) {
    productRoutes.push({
      path: buildPathWithLang(`/products/${productConfig.productId}/analysis`),
      name: `products_${productConfig.storeModule}_liveAnalysis`,
      component: () =>
        productConfig.live.componentPath
          ? import(`../views/products/${productConfig.live.componentPath}`)
          : import("../views/products/DefaultAnalysis.vue"),
      meta: {
        productId: productConfig.productId,
        authorize:
          productConfig.live.allowedRoles ?? productConfig.allowedRoles ?? config.allowedRoles,
        // If the user doesn't have the role to see it, but has the admin right for one of the
        // share class, we should still let access to it.
        allowAdminOfOneShareClass: true,
      },
      props: {
        productId: productConfig.productId,
        lockedBehindLogin: productConfig.lockedBehindLogin,
        storeModule: productConfig.storeModule,
        productName: productConfig.productName,
        productShareClassRoot: productConfig.productShareClassRoot,
        genericCardsToShow: productConfig.live.genericCardsToShow,
        perfStats: productConfig.live.perfStats,
        productColor: productConfig.productColor,
        events: productConfig.live.events,
        allowLogScale: productConfig.live.allowLogScale,
        defaultLogScale: productConfig.live.defaultLogScale,
        disableAnalysisBenchmark: productConfig.live.disableAnalysisBenchmark,
      },
      beforeEnter: beforeEnterChangeLocale,
    });

    // Legacy / bookmarks: keep the old path working.
    productRoutes.push({
      path: buildPathWithLang(`/products/${productConfig.productId}/live-analysis`),
      redirect: (to) => ({
        path: buildPathWithLang(`/products/${productConfig.productId}/analysis`),
        query: to.query,
        hash: to.hash,
      }),
    });

    embeddedProductRoutes.push({
      path: buildPathWithLang(`/products/${productConfig.productId}/embedded/live-perf-graph`),
      name: `products_${productConfig.storeModule}_embedded_livePerfGraph`,
      component: () => import("../views/products/DefaultEmbeddedPerfGraph.vue"),
      meta: {
        productId: productConfig.productId,
        noNeedOfInvestorProfile: true,
      },
      props: {
        productId: productConfig.productId,
        storeModule: productConfig.storeModule,
        productName: productConfig.productName,
        productColor: productConfig.productColor,
        allowLogScale: productConfig.live.allowLogScale,
        defaultLogScale: productConfig.live.defaultLogScale,
      },
      beforeEnter: beforeEnterChangeLocale,
    });

    embeddedProductRoutes.push({
      path: buildPathWithLang(`/products/${productConfig.productId}/embedded/live-stats-table`),
      name: `products_${productConfig.storeModule}_embedded_liveStatsTable`,
      component: () => import("../views/products/DefaultEmbeddedStatsTable.vue"),
      meta: {
        productId: productConfig.productId,
        noNeedOfInvestorProfile: true,
      },
      props: {
        productId: productConfig.productId,
        storeModule: productConfig.storeModule,
        productName: productConfig.productName,
        productColor: productConfig.productColor,
        perfStats: productConfig.live.perfStats,
      },
      beforeEnter: beforeEnterChangeLocale,
    });

    embeddedProductRoutes.push({
      path: buildPathWithLang(`/products/${productConfig.productId}/embedded/live-perf-table`),
      name: `products_${productConfig.storeModule}_embedded_livePerfTable`,
      component: () => import("../views/products/DefaultEmbeddedPerfTable.vue"),
      meta: {
        productId: productConfig.productId,
        noNeedOfInvestorProfile: true,
      },
      props: {
        productId: productConfig.productId,
        storeModule: productConfig.storeModule,
        productName: productConfig.productName,
      },
      beforeEnter: beforeEnterChangeLocale,
    });
  }

  // Only add the Use Case route if it is configured.
  if (productConfig.useCase) {
    // If a redirect is set, just build push path and add the redirect, otherwise build the route.
    if (productConfig.useCase.redirect) {
      productRoutes.push({
        path: buildPathWithLang(`/products/${productConfig.productId}/use-case`),
        redirect: productConfig.useCase.redirect,
      });
    } else {
      productRoutes.push({
        path: buildPathWithLang(`/products/${productConfig.productId}/use-case`),
        name: `products_${productConfig.storeModule}_useCase`,
        component: () => import(`../views/products/${productConfig.useCase.componentPath}`),
        meta: {
          productId: productConfig.productId,
          authorize: productConfig.allowedRoles ?? config.allowedRoles,
          // If the user doesn't have the role to see it, but has the admin right for one of the
          // share class, we should still let access to it.
          allowAdminOfOneShareClass: true,
        },
        props: {
          storeModule: productConfig.storeModule,
        },
        beforeEnter: beforeEnterChangeLocale,
      });
    }
  }

  // Only add the Report routes if it is configured.
  if (productConfig.reports) {
    // Add a redirect in case some users had the Factsheet page saved in a bookmark before we
    // renamed it.
    productRoutes.push({
      path: buildPathWithLang(`/products/${productConfig.productId}/factsheet`),
      redirect: buildPathWithLang(`/products/${productConfig.productId}/report/factsheet`),
    });

    for (const report of productConfig.reports) {
      productRoutes.push({
        path: buildPathWithLang(`/products/${productConfig.productId}/report/${report.id}`),
        name: `products_${productConfig.storeModule}_report_${report.id}`,
        component: () => import("../views/products/DefaultReport.vue"),
        meta: {
          productId: productConfig.productId,
          authorize: report.allowedRoles ?? productConfig.allowedRoles ?? config.allowedRoles,
          // If the user doesn't have the role to see it, but has the admin right for one of the
          // product on the platform, we should still let access to it (so that we match what is
          // shown in the left vertical menu).
          allowAdminOfOneProduct: true,
        },
        props: {
          productId: productConfig.productId,
          productName: productConfig.productName,
          productShortName: productConfig.productShortName,
          productColor: productConfig.productColor,
          productShareClassRoot: productConfig.productShareClassRoot,
          storeModule: productConfig.storeModule,
          needLoginToSee: report.needLoginToSee,
          view: report.id,
        },
        beforeEnter: beforeEnterChangeLocale,
      });

      // We generate a special page that will help us generate the report in pdf.
      // Note that this page won't be referenced somewhere on the platform, and that
      // we have no specific authorization on it.
      productsReportGenerateRoutes.push({
        path: buildPathWithLang(
          `/products/${productConfig.productId}/report-for-pdf-generation/${report.id}`
        ),
        name: `products_${productConfig.storeModule}_report-for-pdf-generation_${report.id}`,
        component: () => import("../views/products/DefaultReportForPdfGeneration.vue"),
        meta: {
          productId: productConfig.productId,
          noNeedOfInvestorProfile: true,
        },
        props: {
          productId: productConfig.productId,
          productName: productConfig.productName,
          productShortName: productConfig.productShortName,
          productColor: productConfig.productColor,
          productShareClassRoot: productConfig.productShareClassRoot,
          storeModule: productConfig.storeModule,
          view: report.id,
        },
        beforeEnter: beforeEnterChangeLocale,
      });
    }
  }

  productRoutes.push({
    path: buildPathWithLang(`/products/${productConfig.productId}/documents`),
    name: `products_${productConfig.storeModule}_documents`,
    component: () => import("../views/products/DefaultDocuments.vue"),
    meta: {
      productId: productConfig.productId,
      authorize: productConfig.allowedRoles ?? config.allowedRoles,
      // If the user doesn't have the role to see it, but has the admin right for one of the
      // share class, we should still let access to it.
      allowAdminOfOneShareClass: true,
    },
    props: {
      productId: productConfig.productId,
      lockedBehindLogin: productConfig.lockedBehindLogin,
      storeModule: productConfig.storeModule,
      productName: productConfig.productName,
      productShareClassRoot: productConfig.productShareClassRoot,
    },
    beforeEnter: beforeEnterChangeLocale,
  });

  productRoutes.push({
    path: buildPathWithLang(`/products/${productConfig.productId}/download-document`),
    name: `products_${productConfig.storeModule}_downloadDocument`,
    component: () => import("../views/products/DefaultDocumentDownload.vue"),
    meta: {
      productId: productConfig.productId,
      authorize: productConfig.allowedRoles ?? config.allowedRoles,
      // If the user doesn't have the role to see it, but has the admin right for one of the
      // share class, we should still let access to it.
      allowAdminOfOneShareClass: true,
    },
    props: {
      productId: productConfig.productId,
      storeModule: productConfig.storeModule,
      productName: productConfig.productName,
    },
    beforeEnter: beforeEnterChangeLocale,
  });

  productRoutes.push({
    path: buildPathWithLang(`/products/${productConfig.productId}/download-last-factsheet`),
    name: `products_${productConfig.storeModule}_downloadLastFactsheet`,
    component: () => import("../views/products/DefaultLastFactsheetDownload.vue"),
    meta: {
      productId: productConfig.productId,
      authorize: productConfig.allowedRoles ?? config.allowedRoles,
      // If the user doesn't have the role to see it, but has the admin right for one of the
      // share class, we should still let access to it.
      allowAdminOfOneShareClass: true,
    },
    props: {
      productId: productConfig.productId,
      storeModule: productConfig.storeModule,
    },
    beforeEnter: beforeEnterChangeLocale,
  });

  productsRoutes.push(productRoutes);
  embeddedProductsRoutes.push(embeddedProductRoutes);
});

productsRoutes = flattenDeep(productsRoutes);
embeddedProductsRoutes = flattenDeep(embeddedProductsRoutes);

// If we want to hide the products, let's avoid creating the routes altogether.
if (config.hideProducts) {
  productsRoutes = [];
  embeddedProductsRoutes = [];
  productsReportGenerateRoutes = [];
}

export { embeddedProductsRoutes, productsReportGenerateRoutes, productsRoutes };
